import api, { normalizeList, buildQueryParams } from "./api";

export const getDocumentTypes = async () => {
  // Request enough to get all types in a single call.
  // KQSPLStandardPagination max_page_size = 100, which is more than enough for a fixed master-data list.
  const response = await api.get("/api/documents/document-types/?page_size=100");
  return normalizeList(response.data);
};

export const uploadDocument = async ({
  file,
  referenceType,
  referenceId,
  documentTypeId,
  description = "",
}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("reference_type", referenceType);
  formData.append("reference_id", referenceId);
  formData.append("document_type_id", documentTypeId);
  if (description) {
    formData.append("description", description);
  }
  const response = await api.post("/api/documents/upload/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const deleteDocument = async (documentId) => {
  const url = `/api/documents/documents/${documentId}/`;
  const response = await api.delete(url);
  return response.data;
};

export const getDocuments = async (filters = {}) => {
  try {
    const queryString = buildQueryParams(filters);
    const url = queryString ? `/api/documents/documents/?${queryString}` : '/api/documents/documents/';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

/**
 * Used by useAllDocuments: params = { page, page_size, search, ordering, ...filters }
 * Returns the full paginated response so useAllDocuments can use total_pages directly
 * and doesn't have to fall back on a calculated estimate.
 */
export const searchDocuments = async (params = {}) => {
  const data = await getDocuments(params);
  if (data && Array.isArray(data.results)) {
    // Pass through the complete DRF paginated response (count, results, total_pages, has_next, …)
    return data;
  }
  // Fallback for non-paginated responses (shouldn't happen in production)
  const list = Array.isArray(data) ? data : [];
  return { results: list, count: list.length, total_pages: 1, has_next: false, has_previous: false };
};

export const getDocumentUrl = async (documentId) => {
  const response = await api.get(`/api/documents/documents/${documentId}/download/`);
  return response.data?.download_url || null;
};

export const previewDocument = async (documentId) => {
  const response = await api.get(`/api/documents/documents/${documentId}/preview/`);
  return { preview_url: response.data?.preview_url, url: response.data?.preview_url, ...response.data };
};

/**
 * Normalize input to items array. Accepts:
 * - Array of { id, filename? }
 * - Array of ids (number)
 */
function normalizeBulkItems(input) {
  if (!input || input.length === 0) return [];
  const list = Array.isArray(input) ? input : [];
  return list.map((item) =>
    typeof item === "object" && item !== null && "id" in item
      ? { id: item.id, filename: item.filename || item.original_filename }
      : { id: item }
  );
}

/**
 * Bulk download: Uses backend endpoint to create ZIP server-side (avoids CORS issues with S3).
 * - One document: downloads single file via backend
 * - Multiple: backend creates ZIP and returns it
 * @param input - Array of document ids or Array<{ id, filename? }>
 * @returns Promise<{ blob: Blob, suggestedFilename: string }>
 */
export const bulkDownloadDocuments = async (input) => {
  const items = normalizeBulkItems(input);
  if (items.length === 0) {
    throw new Error("No documents selected");
  }

  const maxZipFiles = 100;
  if (items.length > maxZipFiles) {
    throw new Error(`Too many documents. Maximum ${maxZipFiles} can be downloaded at once.`);
  }

  // Extract only valid numeric document IDs (filter out placeholder IDs like "flash_placeholder_123")
  const documentIds = items
    .map((item) => item.id)
    .filter((id) => typeof id === "number" || (typeof id === "string" && /^\d+$/.test(id)))
    .map((id) => (typeof id === "string" ? parseInt(id, 10) : id));

  if (documentIds.length === 0) {
    throw new Error("No valid documents to download");
  }

  // For single document, we can still use direct download (opens in new tab)
  if (documentIds.length === 1) {
    const id = documentIds[0];
    const filename = items[0].filename;
    const url = await getDocumentUrl(id);
    if (!url) throw new Error("Could not get download URL");
    // Use window.open for single file to avoid CORS - browser handles download
    window.open(url, '_blank');
    const suggestedFilename = filename && filename.replace(/[/\\]/g, "").trim() ? filename : `document_${id}`;
    // Return a dummy blob since window.open handles it
    return { blob: new Blob(), suggestedFilename };
  }

  // For multiple documents, use backend bulk_download endpoint (creates ZIP server-side)
  try {
    const response = await api.post(
      "/api/documents/documents/bulk_download/",
      { document_ids: documentIds },
      { responseType: "blob" }
    );

    const dateStr = new Date().toISOString().split("T")[0];
    const suggestedFilename = `documents_${dateStr}.zip`;

    return { blob: response.data, suggestedFilename };
  } catch (error) {
    console.error("Bulk download error:", error);
    if (error.response?.status === 404) {
      throw new Error("No accessible documents found");
    }
    throw new Error(error.response?.data?.error || "Failed to download documents");
  }
};

export const getReferenceTypes = async () => {
  return [
    { value: 'service_order', label: 'Service Order' },
    { value: 'user', label: 'User' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'consumable', label: 'Consumable' },
  ];
};

export const getServiceOrdersForFilter = async () => {
  const { getServiceOrders } = await import('./serviceOrders');
  // Fetch up to 100 per page and accumulate all pages so every service order
  // appears as a folder in the "by service order" tree view and in the filter dropdown.
  const PAGE_SIZE = 100;
  let allOrders = [];
  let page = 1;

  while (true) {
    const data = await getServiceOrders({ page_size: PAGE_SIZE, page });
    const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
    allOrders = allOrders.concat(results);

    const hasNext = data?.has_next ?? (results.length === PAGE_SIZE);
    if (!hasNext) break;
    page += 1;
    // Safety cap — avoid infinite loops if the API misbehaves
    if (page > 50) break;
  }

  return allOrders;
};

export const getCompaniesForFilter = async () => {
  const { getCompanies } = await import('./companies');
  const data = await getCompanies({ page_size: 50 });
  return Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
};

export const getUsersForFilter = async () => {
  const { getUsers } = await import('./users');
  const data = await getUsers({ page_size: 50 });
  return Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
};

// =====================================================
// CENTRALIZED DOWNLOAD UTILITIES
// Use these functions for all document downloads across the app
// =====================================================

/**
 * Download a single document by ID
 * Uses the backend to get a presigned URL and opens it in a new tab
 * @param {number|string} documentId - The document ID
 * @param {string} [filename] - Optional filename for the download
 * @returns {Promise<boolean>} - True if download initiated successfully
 */
export const downloadDocument = async (documentId, filename) => {
  try {
    const url = await getDocumentUrl(documentId);
    if (!url) {
      throw new Error("Could not get download URL");
    }
    // Open in new tab - browser will handle the download
    window.open(url, '_blank');
    return true;
  } catch (error) {
    console.error("Download error:", error);
    throw error;
  }
};

/**
 * Download a document using a direct URL (presigned S3 URL)
 * Use this when you already have a download URL
 * @param {string} downloadUrl - The presigned download URL
 * @param {string} [filename] - Optional filename for the download
 * @returns {boolean} - True if download initiated
 */
export const downloadFromUrl = (downloadUrl, filename = 'document') => {
  if (!downloadUrl) {
    console.error("No download URL provided");
    return false;
  }

  // Create a temporary link and click it to trigger download
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return true;
};

/**
 * Helper to trigger download of a blob
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename for the download
 */
export const downloadBlob = (blob, filename) => {
  if (!blob || blob.size === 0) {
    console.warn("Empty blob, skipping download");
    return false;
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  return true;
};

// bulkDownloadDocuments creates one ZIP per call and caps at this many documents per
// call (server-side memory/timeout safety net). downloadDocuments chunks larger
// selections into multiple sequential calls/downloads instead of rejecting them.
const BULK_DOWNLOAD_CHUNK_SIZE = 100;

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

/**
 * Bulk download multiple documents as a ZIP file (or several, if the selection is
 * larger than BULK_DOWNLOAD_CHUNK_SIZE — each chunk is zipped and downloaded
 * separately since a single HTTP response can't stream more than one ZIP).
 * @param {Array} documents - Array of document objects with id and optional filename
 * @param {Object} options - Download options
 * @param {Function} [options.onProgress] - Progress callback (documentsDownloaded, total)
 * @param {Function} [options.onSuccess] - Success callback (totalDownloaded)
 * @param {Function} [options.onError] - Error callback
 * @returns {Promise<boolean>} - True if download completed successfully
 */
export const downloadDocuments = async (documents, options = {}) => {
  const { onProgress, onSuccess, onError } = options;

  try {
    if (!documents || documents.length === 0) {
      throw new Error("No documents selected for download");
    }

    // Filter out invalid documents (placeholders, etc.)
    const validDocuments = documents.filter(doc => {
      const id = doc.id || doc;
      return typeof id === "number" || (typeof id === "string" && /^\d+$/.test(id));
    });

    if (validDocuments.length === 0) {
      throw new Error("No valid documents to download");
    }

    // Normalize to items array
    const items = validDocuments.map(doc => {
      if (typeof doc === "object" && doc !== null) {
        return {
          id: doc.id,
          filename: doc.filename || doc.original_filename || doc.file_name
        };
      }
      return { id: doc };
    });

    const chunks = chunkArray(items, BULK_DOWNLOAD_CHUNK_SIZE);
    const dateStr = new Date().toISOString().split("T")[0];
    let downloaded = 0;

    if (onProgress) onProgress(0, items.length);

    for (let i = 0; i < chunks.length; i += 1) {
      const chunk = chunks[i];
      // Use bulkDownloadDocuments which handles both single and multiple files
      const { blob, suggestedFilename } = await bulkDownloadDocuments(chunk);

      // For single files, window.open is used directly in bulkDownloadDocuments
      // For multiple files, we get a ZIP blob
      if (blob && blob.size > 0) {
        const filename = chunks.length > 1
          ? `documents_${dateStr}_part${i + 1}of${chunks.length}.zip`
          : suggestedFilename;
        downloadBlob(blob, filename);
      }

      downloaded += chunk.length;
      if (onProgress) onProgress(downloaded, items.length);
    }

    if (onSuccess) onSuccess(downloaded);
    return true;
  } catch (error) {
    console.error("Download documents error:", error);
    if (onError) onError(error);
    throw error;
  }
};

// =====================================================
// FULL-TEXT SEARCH
// Search within document content
// =====================================================

/**
 * Full-text search within document content
 * @param {Object} params - Search parameters
 * @param {string} params.q - Search query (required)
 * @param {number} [params.document_type] - Filter by document type ID
 * @param {number} [params.service_order] - Filter by service order ID
 * @param {number} [params.limit=20] - Max results to return
 * @returns {Promise<{ query: string, total_count: number, results: Array }>}
 */
export const fullTextSearch = async (params = {}) => {
  try {
    const queryString = buildQueryParams(params);
    const response = await api.get(`/api/documents/documents/search/?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Full-text search error:', error);
    throw error;
  }
};

/**
 * Trigger text extraction for a document
 * @param {number} documentId - Document ID
 * @returns {Promise<{ status: string, word_count?: number, page_count?: number }>}
 */
export const extractDocumentText = async (documentId) => {
  try {
    const response = await api.post(`/api/documents/documents/${documentId}/extract/`);
    return response.data;
  } catch (error) {
    console.error('Extraction error:', error);
    throw error;
  }
};

/**
 * Trigger text extraction for all pending documents
 * @returns {Promise<{ status: string, processed: number, skipped: number, failed: number }>}
 */
export const extractAllDocuments = async () => {
  try {
    const response = await api.post('/api/documents/documents/extract-all/');
    return response.data;
  } catch (error) {
    console.error('Bulk extraction error:', error);
    throw error;
  }
};
